import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { Calendar } from "lucide-react";
import { formatDateTime } from "@tellescope/utils";
import { Badge } from "@tellescope/ui";
import { EditablePersonCell } from "../cells";

export interface EventContextCardProps {
  calendarEventId: string;
}

export function EventContextCard({ calendarEventId }: EventContextCardProps) {
  const { t } = useTranslation("tickets");

  const eventQuery = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: calendarEventId,
    enabled: !!calendarEventId,
  });

  // Query attendees (users)
  const attendeeIds = eventQuery.data?.attendees ?? [];

  if (eventQuery.isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">{t("Loading event...")}</p>
      </div>
    );
  }

  if (eventQuery.isError || !eventQuery.data) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-sm text-destructive">{t("Unable to load event. Please try again.")}</p>
      </div>
    );
  }

  const event = eventQuery.data;
  const startDate = event.startTimeInMS ? new Date(event.startTimeInMS) : null;
  const endDate =
    event.durationInMinutes && event.startTimeInMS
      ? new Date(event.startTimeInMS + event.durationInMinutes * 60 * 1000)
      : null;

  return (
    <div className="space-y-2 p-4 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Calendar className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Calendar Event")}</h3>
        </div>
        {event.cancelledAt && (
          <Badge variant="destructive" className="text-xs">
            {t("Cancelled")}
          </Badge>
        )}
      </div>

      <div className="space-y-3">
        {event.title && (
          <div>
            <h4 className="text-base font-semibold text-gray-900">{event.title}</h4>
          </div>
        )}

        {startDate && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-20 flex-shrink-0">{t("Start:")}</span>
            <span className="text-sm text-gray-900">{formatDateTime(startDate)}</span>
          </div>
        )}

        {endDate && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-20 flex-shrink-0">{t("End:")}</span>
            <span className="text-sm text-gray-900">{formatDateTime(endDate)}</span>
          </div>
        )}

        {event.description && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-20 flex-shrink-0">{t("Description:")}</span>
            <p className="text-sm text-gray-900 whitespace-pre-wrap flex-1">{event.description}</p>
          </div>
        )}

        {attendeeIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600 w-20 flex-shrink-0">{t("Attendees:")}</span>
            <div className="flex flex-wrap gap-1 flex-1">
              {attendeeIds.map((attendee) => {
                return (
                  <EditablePersonCell
                    key={attendee.id}
                    disabled
                    value={attendee.id}
                    mode={attendee.type}
                    onChange={() => {}}
                    className="px-2 py-1 rounded-sm"
                  />
                );
              })}
            </div>
          </div>
        )}

        {event.location && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-20 flex-shrink-0">{t("Location:")}</span>
            <span className="text-sm text-gray-900">{event.location}</span>
          </div>
        )}
      </div>
    </div>
  );
}
